import React from 'react'
import { Box, Typography } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import { alpha } from '@mui/material/styles'
import ArrowDropUp from '@mui/icons-material/ArrowDropUp'
import classNames from 'classnames'
import useStickyPositionObserver from './useStickyPositionObserver'

// Floating "New" pill shown once the unread divider scrolls out of view and
// sticks to the top of the feed, so the unread boundary stays discoverable.
function NewItemsPill() {
  const styles = useNewItemsPillStyles()
  return (
    <Box className={styles.pill}>
      <ArrowDropUp className={styles.arrow} color="error" />
      <Typography className={styles.text} variant="caption" component="div">
        New
      </Typography>
    </Box>
  )
}

type DividerProps = {
  isOnTop?: boolean
  children?: string
  isHighlighted?: boolean
  showNewIndicator?: boolean
  // Drops the side padding, for a thread whose messages run edge to edge.
  flush?: boolean
}

export default function Divider({ isHighlighted, isOnTop, children, showNewIndicator, flush }: DividerProps) {
  const styles = useDividerStyles()

  // Detects when the (sticky) unread divider has scrolled up out of view so the
  // floating pill can take over. https://usefulangle.com/post/108
  const stickyPositionHelperRef = React.useRef<HTMLDivElement>(null)
  const isSticky = useStickyPositionObserver(showNewIndicator, stickyPositionHelperRef)

  if (showNewIndicator) {
    return (
      <>
        <div ref={stickyPositionHelperRef} />
        <div className={classNames(styles.divider, styles.sticky, { [styles.flush]: flush })}>
          <div className={classNames(styles.newRow, styles.hidable, { [styles.hidden]: isSticky })}>
            {isOnTop && <ArrowDropUp className={styles.inlineArrow} color="error" />}
            <Typography className={styles.newLabel} variant="caption" component="div">
              New
            </Typography>
            <div className={classNames(styles.line, { [styles.highlighted]: isHighlighted })} role="separator" />
            {children && (
              <>
                <Typography className={styles.caption} variant="caption" component="div" color="textSecondary">
                  {children}
                </Typography>
                <div className={classNames(styles.line, { [styles.highlighted]: isHighlighted })} />
              </>
            )}
          </div>
          {isSticky && <NewItemsPill />}
        </div>
      </>
    )
  }

  return (
    <div className={styles.divider}>
      <div className={styles.line} role="separator" />
      {children && (
        <Typography className={styles.caption} variant="caption" component="div" color="textSecondary">
          {children}
        </Typography>
      )}
      <div className={styles.line} />
    </div>
  )
}

const useDividerStyles = makeStyles((theme) => ({
  divider: {
    padding: theme.spacing(1, 2),
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1,
  },
  flush: {
    paddingLeft: 0,
    paddingRight: 0,
  },
  newRow: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
  },
  newLabel: {
    color: theme.palette.error.main,
    paddingRight: theme.spacing(1.5),
    whiteSpace: 'nowrap',
  },
  inlineArrow: {
    marginRight: theme.spacing(-0.5),
  },
  caption: {
    padding: theme.spacing(1),
  },
  line: {
    backgroundColor: theme.palette.divider,
    flexGrow: 1,
    height: 1,
  },
  highlighted: {
    backgroundColor: theme.palette.error.main,
  },
  sticky: {
    position: 'sticky',
    top: 0,
  },
  hidable: {
    opacity: 1,
    transition: 'opacity 0.3s ease-in-out',
  },
  hidden: {
    opacity: '0 !important',
  },
}))

const PILL_WIDTH = 5.5
const useNewItemsPillStyles = makeStyles((theme) => ({
  pill: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'absolute',
    top: 0,
    left: '50%',
    transform: 'translateX(-50%)',
    width: theme.spacing(PILL_WIDTH),
    height: theme.spacing(PILL_WIDTH),
    backgroundColor: alpha(theme.palette.background.default, 0.87),
    borderRadius: '50%',
    backdropFilter: 'blur(2px)',
  },
  text: {
    color: theme.palette.error.main,
  },
  arrow: {
    marginTop: theme.spacing(-0.5),
    marginBottom: theme.spacing(-1.25),
  },
}))
